import { useCallback, useRef, useEffect } from "preact/hooks";
import { api, Bookmark } from "../utils/api";

interface PrefetchCache {
  bookmarks: Map<string, { data: Bookmark; timestamp: number }>;
  nextPage: { data: Bookmark[]; offset: number; timestamp: number } | null;
}

const CACHE_TTL = 30000; // 30 seconds
const cache: PrefetchCache = {
  bookmarks: new Map(),
  nextPage: null,
};

export function usePrefetch(currentBookmarks: Bookmark[], search?: string) {
  const prefetchedOffsets = useRef<Set<number>>(new Set());
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear cache when search changes
  useEffect(() => {
    cache.nextPage = null;
    prefetchedOffsets.current.clear();
  }, [search]);

  // Prefetch next page of bookmarks
  const prefetchNextPage = useCallback(async () => {
    const nextOffset = currentBookmarks.length;

    // Skip if already prefetched this offset
    if (prefetchedOffsets.current.has(nextOffset)) {
      return cache.nextPage?.data || [];
    }

    // Skip if cache is still valid
    if (
      cache.nextPage &&
      cache.nextPage.offset === nextOffset &&
      Date.now() - cache.nextPage.timestamp < CACHE_TTL
    ) {
      return cache.nextPage.data;
    }

    try {
      const response = await api.getBookmarks({
        search: search || undefined,
        limit: 50,
        offset: nextOffset,
      });

      cache.nextPage = {
        data: response.data,
        offset: nextOffset,
        timestamp: Date.now(),
      };
      prefetchedOffsets.current.add(nextOffset);

      return response.data;
    } catch {
      return [];
    }
  }, [currentBookmarks.length, search]);

  // Prefetch a single bookmark's details
  const prefetchBookmark = useCallback(async (id: string) => {
    // Check if already cached and fresh
    const cached = cache.bookmarks.get(id);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      const response = await api.getBookmark(id);
      cache.bookmarks.set(id, {
        data: response.data,
        timestamp: Date.now(),
      });
      return response.data;
    } catch {
      return null;
    }
  }, []);

  // Prefetch on hover with delay to avoid excessive requests
  const prefetchOnHover = useCallback(
    (id: string) => {
      // Clear any pending prefetch
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Delay prefetch to avoid prefetching on quick mouse movements
      hoverTimeoutRef.current = setTimeout(() => {
        prefetchBookmark(id);
      }, 150);
    },
    [prefetchBookmark]
  );

  // Cancel prefetch on mouse leave
  const cancelPrefetch = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // Get cached bookmark if available
  const getCachedBookmark = useCallback((id: string): Bookmark | null => {
    const cached = cache.bookmarks.get(id);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }
    return null;
  }, []);

  // Get prefetched next page if available
  const getPrefetchedNextPage = useCallback((): Bookmark[] | null => {
    if (
      cache.nextPage &&
      cache.nextPage.offset === currentBookmarks.length &&
      Date.now() - cache.nextPage.timestamp < CACHE_TTL
    ) {
      return cache.nextPage.data;
    }
    return null;
  }, [currentBookmarks.length]);

  // Clear all caches
  const clearCache = useCallback(() => {
    cache.bookmarks.clear();
    cache.nextPage = null;
    prefetchedOffsets.current.clear();
  }, []);

  return {
    prefetchNextPage,
    prefetchBookmark,
    prefetchOnHover,
    cancelPrefetch,
    getCachedBookmark,
    getPrefetchedNextPage,
    clearCache,
  };
}

// Hook to trigger prefetch when element is near viewport
export function useIntersectionPrefetch(
  callback: () => void,
  options: { threshold?: number; rootMargin?: string } = {}
) {
  const ref = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            callbackRef.current();
          }
        });
      },
      {
        threshold: options.threshold || 0,
        rootMargin: options.rootMargin || "200px",
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [options.threshold, options.rootMargin]);

  return ref;
}
